<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
  import { usePreferredReducedMotion } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import { useDraftActions } from '@/composition/menu-import/use-draft-actions';
  import {
    useDishFields,
    useOptimisticValue,
  } from '@/composition/menu-import/use-dish-fields';
  import { canKeepToday } from '@/domain/menu-import/live-rows';
  import type { IDraftItem, IMenuDraft } from '@/interfaces/IMenuImport';
  import V2MenuLivePriceFields from './V2MenuLivePriceFields.vue';

  /**
   * The editor of a dish already on the menu (plan §3.5 «Editor of a linked
   * dish»), right below its row: the menu's name, «Este plato ya está en su
   * menú.», its price or size prices with today's, «Oculto», «Dejar el precio
   * de hoy», «No es este plato» (a new dish, or another dish of the menu),
   * «Ver en la carta» and «Listo». Publishing changes only the prices and
   * whether it is hidden, so nothing else is offered. The name shown is the
   * one the editor opened with (a delta that removes the link switches
   * editors once the owner leaves this one).
   */
  const props = defineProps<{
    /** A linked dish (`live.dish` set when it opens). */
    item: IDraftItem;
    draft: IMenuDraft;
    currency: string;
    country: string;
    /** The dish was read from a page of the menu («Ver en la carta»). */
    hasSource: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'see-source'): void;
    /** The owner linked it elsewhere or made it new: the row picks the other editor now. */
    (e: 'relinked'): void;
    (e: 'close'): void;
  }>();
  const ctx = useMenuImport();
  const actions = useDraftActions({ country: () => props.country });
  const reducedMotion = usePreferredReducedMotion();

  const root = ref<HTMLElement | null>(null);
  let alive = true;
  /** The link when the editor opened: it renders from it while a removed link waits for the switch. */
  const openedLive = props.item.live;
  const dishName = openedLive?.dish?.name ?? props.item.name;
  /** The dish with its link (the one it opened with, once the link is gone). */
  const viewItem = computed<IDraftItem>(() =>
    props.item.live?.dish || !openedLive
      ? props.item
      : { ...props.item, live: openedLive }
  );

  const fields = useDishFields({
    item: () => props.item,
    country: () => props.country,
  });
  const { form, errors } = fields;
  const { value: hidden, set: setHiddenValue } = useOptimisticValue(
    () => props.item.hidden
  );

  const sized = computed(() => form.value.priceSizes.rows.length > 0);
  const canKeep = computed(() => canKeepToday(viewItem.value));
  const canPickOther = computed(
    () => (props.draft.live?.others.length ?? 0) > 0
  );

  function onPriceSizesFocus(inside: boolean) {
    if (inside) {
      fields.focusField('priceSizes');
      return;
    }
    fields.blurField('priceSizes');
    const result = fields.commit(['priceSizes']);
    if (result.status === 'valid' && result.ops.length > 0)
      void ctx.mutate(result.ops);
  }

  function setHidden(value: boolean) {
    setHiddenValue(
      value,
      ctx.mutate([{ op: 'updateItem', itemId: props.item.id, hidden: value }])
    );
  }

  /** «Dejar el precio de hoy»: the photo's price (or size prices) is dropped. */
  function keepToday() {
    if (props.item.sizes.length > 0) void actions.keepLiveSizes(props.item.id);
    else void actions.keepLivePrice(props.item.id);
  }

  /** «Es un plato nuevo»: published as a new dish. */
  async function makeNew(close: () => void) {
    close();
    const linked = await actions.linkLive(props.item.id, null);
    if (linked && alive) emit('relinked');
  }

  /** «Es otro de mi menú…»: the owner picks the right dish. */
  async function pickOther(close: () => void) {
    close();
    const linked = await actions.pickLive(props.item.id);
    if (linked && alive) emit('relinked');
  }

  /** The focused field inside the editor is left, which saves it. */
  function blurInside() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.value?.contains(active))
      active.blur();
  }

  /** «Listo»: saves the price being typed; stays open while it is wrong. */
  function done() {
    blurInside();
    if (errors.value.priceSizes !== null) return;
    emit('close');
  }

  // Opened at the bottom of the screen: bring it into view; opened higher up,
  // its end («Listo») scrolls just above the floating «Pedir cambios» (the
  // list's scroll padding).
  onMounted(async () => {
    await nextTick();
    if (!alive || !root.value) return;
    const top = root.value.getBoundingClientRect().top;
    const row = root.value.closest('[data-item-id]');
    const behavior = reducedMotion.value === 'reduce' ? 'auto' : 'smooth';
    if (row && (top < 0 || top > window.innerHeight * 0.75))
      row.scrollIntoView({ block: 'start', behavior });
    else root.value.scrollIntoView({ block: 'nearest', behavior });
  });

  onBeforeUnmount(() => {
    // Closing while typing still saves what was typed.
    blurInside();
    alive = false;
  });
</script>

<template>
  <section ref="root" class="v2-menu-live-item-editor" :aria-label="dishName">
    <header class="editor-head">
      <p class="head-name">{{ dishName }}</p>
      <p class="head-text">{{ $t('menuImport.update.editorHeader') }}</p>
    </header>
    <div class="editor-grid">
      <V2MenuLivePriceFields
        :class="{ 'span-2': sized }"
        :item="viewItem"
        :value="form.priceSizes"
        :errors="errors.priceSizes"
        :currency="currency"
        :country="country"
        @input="fields.setPriceSizes"
        @focus-change="onPriceSizesFocus"
      />
      <div class="editor-side" :class="{ 'is-beside': !sized }">
        <PosUiSwitch
          :value="hidden"
          :label="$t('menuImport.item.hidden')"
          @input="setHidden"
        />
      </div>
    </div>
    <div class="editor-links">
      <button v-if="canKeep" type="button" class="text-link" @click="keepToday">
        {{ $t('menuImport.update.keepToday') }}
      </button>
      <PosUiPopover placement="bottom">
        <template #trigger="{ open, toggle }">
          <button
            type="button"
            class="text-link"
            aria-haspopup="true"
            :aria-expanded="open ? 'true' : 'false'"
            @click="toggle"
          >
            {{ $t('menuImport.update.notThisDish') }}
          </button>
        </template>
        <template #default="{ close }">
          <div class="link-menu">
            <button
              type="button"
              class="link-menu-item"
              @click="makeNew(close)"
            >
              {{ $t('menuImport.update.makeNew') }}
            </button>
            <button
              v-if="canPickOther"
              type="button"
              class="link-menu-item"
              @click="pickOther(close)"
            >
              {{ $t('menuImport.update.pickOther') }}
            </button>
          </div>
        </template>
      </PosUiPopover>
    </div>
    <p class="editor-note">{{ $t('menuImport.update.onlyPriceNote') }}</p>
    <footer class="editor-foot">
      <PosUiButton
        v-if="hasSource"
        color="neutral"
        variant="ghost"
        icon="image-search-outline"
        @click="emit('see-source')"
        >{{ $t('menuImport.editor.seeOnMenu') }}</PosUiButton
      >
      <PosUiButton color="neutral" class="foot-done" @click="done">{{
        $t('menuImport.editor.close')
      }}</PosUiButton>
    </footer>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-live-item-editor {
    @apply tw-mt-2 tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-p-4 tw-shadow-sm;

    .editor-head {
      @apply tw-flex tw-flex-col tw-gap-0.5;
    }
    .head-name {
      @apply tw-m-0 tw-break-words tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .head-text {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500;
    }
    // One column on phones, two from tablets: sizes take the whole width.
    .editor-grid {
      @apply tw-grid tw-grid-cols-1 tw-items-start tw-gap-4 md:tw-grid-cols-2;
      .span-2 {
        @apply md:tw-col-span-2;
      }
    }
    .editor-side {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
      // Beside the price field: level with its input.
      &.is-beside {
        @apply md:tw-pt-7;
      }
    }
    .editor-links {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-5;
    }
    .text-link {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-neutral-700 tw-underline tw-underline-offset-4 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .link-menu {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .link-menu-item {
      @apply tw-min-h-11 tw-w-full tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-neutral-800 hover:tw-bg-neutral-100;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .editor-note {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .editor-foot {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-3;
    }
    .foot-done {
      @apply tw-ml-auto;
    }
  }
</style>
